/**
 * Tabela ecommerce: 1 linha = 1 transação documental.
 * WhatsApp individual removido — lote fica na toolbar.
 * @module EcommercePatientsTable
 */

import { memo, useState } from "react";
import {
  Checkbox,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import type { Patient } from "fhir/r5";
import { PrimaryButton } from "@/components/common";
import { useFormatter } from "@/utils/useFormatter";
import {
  statsTableCellTextSx,
  statsTableContainerSx,
  statsTableHeadCellSx,
  statsTableSx,
  statsTableTruncatedCellSx,
} from "@/pages/adminInsights/utils/statsTableStyles";
import {
  getTransactionEcommerceStatusChip,
  type DocumentTransactionRow,
} from "src-next/modules/ecommerce";
import { getTransactionDisplayLabel } from "src-next/modules/ecommerce/helpers/buildDocumentTransactionRows";
import {
  getPatientCpf,
  getPatientDisplayName,
} from "./orders/wooFormatters";

const ROWS_PER_PAGE_OPTIONS = [8, 16, 32];

interface EcommercePatientsTableProps {
  rows: DocumentTransactionRow[];
  selectedKeys: string[];
  getRowKey: (row: DocumentTransactionRow) => string;
  onToggleRow: (key: string) => void;
  page: number;
  onPageChange: (page: number) => void;
  showStatusColumn?: boolean;
  onManage: (patient: Patient, transactionId: string) => void;
}

export default memo(function EcommercePatientsTable({
  rows,
  selectedKeys,
  getRowKey,
  onToggleRow,
  page,
  onPageChange,
  showStatusColumn = false,
  onManage,
}: EcommercePatientsTableProps) {
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);

  const paginated = rows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Stack spacing={0}>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          ...statsTableContainerSx,
          borderColor: "primary.light",
        }}
      >
        <Table size="medium" sx={statsTableSx}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" width={48} sx={statsTableHeadCellSx} />
              <TableCell sx={{ ...statsTableHeadCellSx, width: "28%" }}>
                Paciente
              </TableCell>
              <TableCell sx={{ ...statsTableHeadCellSx, width: 140 }}>
                CPF
              </TableCell>
              <TableCell sx={{ ...statsTableHeadCellSx, width: 140 }}>
                Transação
              </TableCell>
              <TableCell sx={{ ...statsTableHeadCellSx, width: 170 }}>
                Status
              </TableCell>
              <TableCell
                align="center"
                sx={{ ...statsTableHeadCellSx, width: 160 }}
              >
                Ações
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginated.map((row) => {
              const key = getRowKey(row);
              const name = getPatientDisplayName(row.patient);
              const selected = selectedKeys.includes(key);
              const statusChip = getTransactionEcommerceStatusChip(row.opState);
              const txLabel = getTransactionDisplayLabel(row);

              return (
                <TableRow key={key} hover selected={selected}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      checked={selected}
                      onChange={() => onToggleRow(key)}
                      inputProps={{ "aria-label": `Selecionar ${name}` }}
                    />
                  </TableCell>
                  <TableCell sx={statsTableTruncatedCellSx}>
                    <Tooltip title={name}>
                      <Typography fontWeight={600} sx={statsTableCellTextSx} noWrap>
                        {name}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={statsTableTruncatedCellSx}>
                    <Typography color="text.secondary" sx={statsTableCellTextSx} noWrap>
                      {useFormatter.formatCPF(getPatientCpf(row.patient)) ||
                        "Não informado"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={statsTableTruncatedCellSx}>
                    <Typography sx={statsTableCellTextSx} noWrap>
                      {txLabel}
                    </Typography>
                  </TableCell>
                  <TableCell sx={statsTableTruncatedCellSx}>
                    {showStatusColumn ? (
                      <Chip
                        size="small"
                        label={statusChip.label}
                        color={statusChip.color}
                        variant="outlined"
                        sx={{
                          height: 26,
                          fontWeight: 600,
                          maxWidth: "100%",
                          "& .MuiChip-label": {
                            px: 1,
                            fontSize: "0.75rem",
                          },
                        }}
                      />
                    ) : (
                      <Skeleton
                        variant="rounded"
                        width={120}
                        height={26}
                        sx={{ borderRadius: 8 }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Stack
                      direction="row"
                      spacing={0.75}
                      justifyContent="center"
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Tooltip title="Verificar documentos e liberar compra">
                        <span>
                          <PrimaryButton
                            width="fit-content"
                            height="36px"
                            startIcon={<DescriptionOutlinedIcon fontSize="small" />}
                            onClick={() =>
                              onManage(row.patient, row.transactionId)
                            }
                          >
                            Gerenciar
                          </PrimaryButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={(_, nextPage) => onPageChange(nextPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(Number(event.target.value));
          onPageChange(0);
        }}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        labelRowsPerPage="Linhas por página"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}–${to} de ${count}`
        }
      />
    </Stack>
  );
});
